/**
 * AuraTemplateBuilderScreen - Create and manage templates
 *
 * Route: /aura/template-builder
 *
 * Features:
 * - Create new templates with HTML editor
 * - Preview templates in real-time
 * - Set template metadata (name, category, tags)
 * - Bulk upload templates from JSON
 */
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, Input } from "@aix/ui"
import { ChevronLeft, Plus, Save, Eye, Code, Upload, Sparkles, LayoutTemplate, Trash2, Copy, Check } from "lucide-react"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"

// Template categories
const CATEGORIES = [
  { value: "landing", label: "Landing Page" },
  { value: "pricing", label: "Pricing" },
  { value: "features", label: "Features" },
  { value: "about", label: "About" },
  { value: "contact", label: "Contact" },
  { value: "blog", label: "Blog" },
  { value: "portfolio", label: "Portfolio" },
  { value: "dashboard", label: "Dashboard" },
  { value: "auth", label: "Authentication" },
  { value: "other", label: "Other" },
] as const

type ViewMode = "edit" | "preview" | "split"

const AuraTemplateBuilderScreen = () => {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>("split")
  const [copied, setCopied] = useState(false)

  // Form state
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [category, setCategory] = useState<string>("landing")
  const [description, setDescription] = useState("")
  const [tags, setTags] = useState("")
  const [htmlContent, setHtmlContent] = useState(DEFAULT_HTML)
  const [cssContent, setCssContent] = useState("")
  const [isFeatured, setIsFeatured] = useState(false)

  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [bulkJson, setBulkJson] = useState("")

  // Create template mutation
  const createMutation = trpc.auraTemplates.createTemplate.useMutation({
    onSuccess: (template) => {
      toast.success(`Template "${template.name}" created!`)
      // Reset form
      setName("")
      setSlug("")
      setDescription("")
      setTags("")
      setHtmlContent(DEFAULT_HTML)
      setCssContent("")
      setIsFeatured(false)
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create template")
    },
  })

  // Bulk create mutation
  const bulkCreateMutation = trpc.auraTemplates.bulkCreateTemplates.useMutation({
    onSuccess: (result) => {
      toast.success(`Created ${result.count} templates!`)
      setBulkJson("")
      setShowBulkUpload(false)
    },
    onError: (error) => {
      toast.error(error.message || "Failed to bulk create templates")
    },
  })

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value)
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(value))
    }
  }

  const generateSlug = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")

  const handleCreateTemplate = () => {
    if (!name.trim()) {
      toast.error("Please enter a template name")
      return
    }
    if (!slug.trim()) {
      toast.error("Please enter a slug")
      return
    }
    if (!htmlContent.trim()) {
      toast.error("Please enter HTML content")
      return
    }

    createMutation.mutate({
      name: name.trim(),
      slug: slug.trim(),
      category: category as any,
      description: description.trim() || undefined,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      html_content: htmlContent,
      css_content: cssContent || undefined,
      is_featured: isFeatured,
      is_public: true,
    })
  }

  const handleBulkUpload = () => {
    try {
      const templates = JSON.parse(bulkJson)
      if (!Array.isArray(templates)) {
        toast.error("JSON must be an array of templates")
        return
      }
      bulkCreateMutation.mutate({ templates })
    } catch (e) {
      toast.error("Invalid JSON format")
    }
  }

  const copyHtml = () => {
    void navigator.clipboard.writeText(htmlContent)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/aura" })}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-primary" />
              <h1 className="text-xl font-semibold text-foreground">Template Builder</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowBulkUpload(!showBulkUpload)}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button size="sm" onClick={handleCreateTemplate} disabled={createMutation.isLoading}>
              {createMutation.isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Template
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Upload Panel */}
      {showBulkUpload && (
        <div className="border-b border-border bg-secondary/30 p-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-medium text-foreground mb-2">Bulk Upload Templates</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Paste a JSON array of templates. Each template needs: name, slug, category, html_content
            </p>
            <textarea
              value={bulkJson}
              onChange={(e) => setBulkJson(e.target.value)}
              placeholder={`[
  {
    "name": "Hero Section",
    "slug": "hero-section",
    "category": "landing",
    "description": "A modern hero section",
    "tags": ["hero", "landing"],
    "html_content": "<section>...</section>",
    "is_featured": true
  }
]`}
              className="w-full h-40 bg-background border border-border rounded-md p-3 font-mono text-sm resize-none"
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setShowBulkUpload(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleBulkUpload} disabled={bulkCreateMutation.isLoading || !bulkJson.trim()}>
                {bulkCreateMutation.isLoading ? "Uploading..." : "Upload Templates"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Template Settings */}
        <div className="w-80 border-r border-border bg-secondary/30 p-4 overflow-y-auto">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">Template Settings</h3>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Name *</label>
              <Input
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., SaaS Landing Hero"
              />
            </div>

            {/* Slug */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Slug *</label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g., saas-landing-hero" />
            </div>

            {/* Category */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Category *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the template..."
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm resize-none h-20"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Tags</label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="saas, dark, gradient (comma separated)"
              />
            </div>

            {/* Featured */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="featured"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="rounded border-border"
              />
              <label htmlFor="featured" className="text-sm font-medium text-foreground flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-primary" />
                Featured Template
              </label>
            </div>

            {/* CSS Content */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Custom CSS (optional)</label>
              <textarea
                value={cssContent}
                onChange={(e) => setCssContent(e.target.value)}
                placeholder="/* Custom styles */"
                className="w-full bg-background border border-border rounded-md px-3 py-2 font-mono text-xs resize-none h-24"
              />
            </div>
          </div>
        </div>

        {/* Main Editor Area */}
        <div className="flex-1 flex flex-col">
          {/* View Mode Toggle */}
          <div className="border-b border-border px-4 py-2 flex items-center justify-between bg-background">
            <div className="flex items-center gap-1">
              <Button variant={viewMode === "edit" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("edit")}>
                <Code className="w-4 h-4 mr-1" />
                Code
              </Button>
              <Button
                variant={viewMode === "split" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("split")}
              >
                Split
              </Button>
              <Button
                variant={viewMode === "preview" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("preview")}
              >
                <Eye className="w-4 h-4 mr-1" />
                Preview
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={copyHtml}>
              {copied ? <Check className="w-4 h-4 mr-1 text-green-500" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? "Copied!" : "Copy HTML"}
            </Button>
          </div>

          {/* Editor / Preview */}
          <div className="flex-1 flex overflow-hidden">
            {/* Code Editor */}
            {(viewMode === "edit" || viewMode === "split") && (
              <div className={`${viewMode === "split" ? "w-1/2 border-r border-border" : "w-full"} flex flex-col`}>
                <textarea
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  className="flex-1 bg-background p-4 font-mono text-sm resize-none focus:outline-hidden"
                  placeholder="Enter your HTML with Tailwind classes..."
                  spellCheck={false}
                />
              </div>
            )}

            {/* Preview */}
            {(viewMode === "preview" || viewMode === "split") && (
              <div className={`${viewMode === "split" ? "w-1/2" : "w-full"} bg-white overflow-auto`}>
                <iframe
                  srcDoc={`
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://cdn.tailwindcss.com"></script>
  <style>${cssContent}</style>
</head>
<body>
  ${htmlContent}
</body>
</html>
                  `}
                  className="w-full h-full border-0"
                  title="Template Preview"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Default HTML template
const DEFAULT_HTML = `<section class="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
  <div class="max-w-6xl mx-auto px-4 text-center">
    <h1 class="text-5xl font-bold mb-6">
      Build Something Amazing
    </h1>
    <p class="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
      Start with this template and customize it to create your perfect landing page.
    </p>
    <div class="flex gap-4 justify-center">
      <button class="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-lg font-semibold transition-colors">
        Get Started
      </button>
      <button class="border border-gray-600 hover:border-gray-500 px-8 py-3 rounded-lg font-semibold transition-colors">
        Learn More
      </button>
    </div>
  </div>
</section>`

export default AuraTemplateBuilderScreen
